import { defer, periodicity, calculateFramesOffset, getProgress, getCurrentFrame } from './utils';
import Timeline from '../sprite-timeline';
import { parseEasing } from './easing';

const _timing = Symbol('timing'),
    _keyframes = Symbol('keyframes'),
    _initState = Symbol('initState'),
    _readyDefer = Symbol('readyDefer'),
    _finishedDefer = Symbol('finishedDefer'),
    _effects = Symbol('effects'),
    _activeReadyTimer = Symbol('activeReadyTimer'),
    _activeFinishTimer = Symbol('activeFinishTimer'),
    _removeDefer = Symbol('removeDefer');

/**
  easing: {
    type: 'cubic-bezier',
    value: [...]
  }
  easing: {
    type: 'steps',
    step: 1,
    pos: 'end'
  }
 */
const defaultTiming = {
    delay: 0,
    endDelay: 0,
    fill: 'auto',
    iterations: 1.0,
    playbackRate: 1.0,
    direction: 'normal',
    easing: 'linear',
    effect: null,
};

/**
  animation: play --> delay --> effect --> endDelay
  playState: idle --> pending --> running --> pending --> finished
 */
export default class {
    constructor(initState, keyframes, timing) {
        if (Array.isArray(initState)) {
            // 如果 initState 缺省，默认 keyframes 的第一帧为 initState
            [initState, keyframes, timing] = [initState[0], initState, keyframes];
        }

        if (typeof timing === 'number') {
            timing = { duration: timing };
        }

        this[_timing] = Object.assign({}, defaultTiming, timing);
        this[_timing].easing = parseEasing(this[_timing].easing);
        this[_keyframes] = calculateFramesOffset(keyframes);

        const lastFrame = this[_keyframes][this[_keyframes].length - 1];

        this[_initState] = {}; // 初始状态

        Object.keys(lastFrame).forEach(key => {
            if (Object.prototype.hasOwnProperty.call(initState, key)) {
                this[_initState][key] = initState[key];
            }
        });

        if (this[_keyframes][0].offset !== 0) {
            // 要补第一帧
            this[_keyframes].unshift(Object.assign({}, this[_initState], { offset: 0 }));
        }
        if (lastFrame.offset < 1) {
            // 要补最后一帧
            this[_keyframes].push(Object.assign({}, lastFrame, { offset: 1 }));
        }

        this[_effects] = {};
        this.timeline = null; // idle, no effect
    }

    getPlaybackRate() {
        return this[_timing].playbackRate;
    }

    setPlaybackRate(rate) {
        if (this.timeline) {
            this.timeline.setPlaybackRate(rate);
        }
        this[_timing].playbackRate = rate;
    }

    getPlayState() {
        const timeline = this.timeline,
            { iterations, duration, endDelay } = this[_timing];
        let state = 'running';

        if (timeline == null) {
            state = 'idle';
        } else if (timeline.getPaused()) {
            state = 'paused';
        } else if (timeline.getCurrentTime() < 0) {
            // 开始 pending
            state = 'pending';
        } else {
            const ed = timeline.getCurrentTime() - iterations * duration;
            if (ed > 0 && ed < endDelay) {
                // 结束 pending
                state = 'pending';
            } else if (ed >= endDelay) {
                state = 'finished';
            }
        }
        return state;
    }

    getProgress() {
        if (!this.timeline) return 0;

        const { duration, iterations } = this[_timing];
        const timeline = this.timeline,
            playState = this.getPlayState();

        let p;

        if (playState === 'idle') {
            p = 0;
        } else if (playState === 'paused' && timeline.getCurrentTime() < 0) {
            p = 0;
        } else if (playState === 'pending') {
            if (timeline.getCurrentTime() < 0) {
                p = 0;
            } else {
                const time = timeline.seekLocalTime(iterations * duration);
                p = periodicity(time, duration)[1] / duration;
            }
        } else if (playState === 'running' || playState === 'paused') {
            p = periodicity(timeline.getCurrentTime(), duration)[1] / duration;
        }

        if (playState === 'finished') {
            p = periodicity(iterations, 1)[1];
        }

        return p;
    }

    getFrame() {
        const playState = this.getPlayState(),
            initState = this[_initState],
            { fill } = this[_timing];

        if (playState === 'idle') {
            return initState;
        }

        const { currentTime } = this.timeline,
            keyframes = this[_keyframes].slice(0);

        const { p, inverted } = getProgress(this.timeline, this[_timing], this.getProgress());

        let frameState = initState;
        if (currentTime < 0 && playState === 'pending') {
            // 在开始前 delay 阶段
            if (fill === 'backwards' || fill === 'both') {
                frameState = inverted ? keyframes[keyframes.length - 1] : keyframes[0];
            }
        } else if ((playState !== 'pending' && playState !== 'finished') || fill === 'forwards' || fill === 'both') {
            // 不在 endDelay 或结束状态，或 forwards
            frameState = getCurrentFrame(this[_timing], keyframes, this[_effects], p);
        }
        return frameState;
    }

    pause() {
        this.timeline.setPlaybackRate(0);
    }

    setBaseTimeline(timeline) {
        this[_timing].timeline = timeline;
    }

    getBaseTimeline() {
        return this[_timing].timeline;
    }

    [_activeReadyTimer]() {
        if (this[_readyDefer] && !this[_readyDefer].timerID) {
            if (this.timeline.getCurrentTime() < 0) {
                this[_readyDefer].timerID = this.timeline.setTimeout(
                    () => {
                        this[_readyDefer].resolve();
                        delete this[_readyDefer];
                    },
                    { delay: -this.timeline.getCurrentTime(), heading: false },
                );
            } else {
                this[_readyDefer].timerID = this.timeline.setTimeout(
                    () => {
                        this[_readyDefer].resolve();
                        delete this[_readyDefer];
                    },
                    { delay: 0, isEntropy: true },
                );
            }
        }
    }

    [_activeFinishTimer]() {
        const { duration, iterations, endDelay } = this[_timing];
        const delay = duration * iterations + endDelay;
        if (this[_finishedDefer] && !this[_finishedDefer].timerID) {
            this[_finishedDefer].timerID = this.timeline.setTimeout(
                () => {
                    this[_finishedDefer].resolve();
                },
                { delay: delay - this.timeline.getCurrentTime(), heading: false },
            );
        }
    }

    play() {
        if (this.getPlayState() === 'finished') {
            this.cancel();
        }

        if (this.getPlayState() === 'idle') {
            if (this.getPlaybackRate() <= 0) {
                return;
            }
            const { delay, playbackRate, timeline } = this[_timing];
            this.timeline = new Timeline(
                {
                    originTime: delay,
                    playbackRate,
                },
                timeline,
            );
            this[_activeReadyTimer]();
            this[_activeFinishTimer]();
        } else if (this.getPlayState() === 'paused') {
            this.timeline.setPlaybackRate(this.getPlaybackRate);
            this[_activeReadyTimer]();
        }
    }

    [_removeDefer](deferID, complete = false) {
        const defered = this[deferID],
            { timeline } = this;

        if (defered && timeline) {
            timeline.clearTimeout(defered.timerID);
            if (complete) {
                defered.resolve();
            }
        }
        delete this[deferID];
    }

    cancel() {
        this[_removeDefer](_readyDefer);
        this[_removeDefer](_finishedDefer);
        this.timeline = null;
    }

    finish() {
        this.timeline.setCurrentTime(Infinity);
        this[_removeDefer](_readyDefer);
        this[_removeDefer](_finishedDefer, true);
    }

    applyEffects(effects) {
        return Object.assign(this[_effects], effects);
    }

    getReady() {
        if (this[_readyDefer]) {
            return this[_readyDefer].promise;
        }

        if (this.timeline && this.timeline.getCurrentTime() >= 0) {
            if (this.getPlayState() !== 'paused') {
                return Promise.resolve();
            }
        }

        this[_readyDefer] = defer();
        if (this.timeline) {
            // 已经在 pending 状态
            this[_activeReadyTimer]();
        }
        if (this[_readyDefer]) {
            return this[_readyDefer].promise;
        }
        return Promise.resolve();
    }

    getFinished() {
        if (this.getPlayState() === 'finished') {
            return Promise.resolve();
        }
        if (!this[_finishedDefer]) {
            this[_finishedDefer] = defer();

            if (this.timeline) {
                this[_activeFinishTimer]();
            }
        }

        return this[_finishedDefer].promise;
    }
}
